import React, { useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { ScanFace, Trash2, ExternalLink, Clock, GitCompareArrows, Check, X, Plus } from 'lucide-react';
import { t, tf } from '../../lib/i18n';

const formatDate = (value, language) => new Date(value).toLocaleDateString(
  language === 'fr' ? 'fr-FR' : 'en-US',
  { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' },
);

const Thumb = ({ b64, label, variant }) => (
  <div className="relative flex-1 aspect-square bg-black/40 overflow-hidden">
    {b64
      ? <img src={`data:image/jpeg;base64,${b64}`} alt={label} className="w-full h-full object-cover" loading="lazy" />
      : <div className="w-full h-full flex items-center justify-center text-white/20"><ScanFace className="w-6 h-6" /></div>}
    <span className={`absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-semibold ${variant === 'after' ? 'bg-lux-gold/90 text-[var(--lux-ink)]' : 'bg-black/70 text-white'}`}>{label}</span>
  </div>
);

const NewSimulationButton = ({ language, onNew, testId }) => (
  <Button size="sm" onClick={onNew} className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] active:scale-[0.98] transition-[background-color,transform] duration-200 h-9" data-testid={testId}>
    <Plus className="w-4 h-4 mr-1" /> {t('newSimulation', language)}
  </Button>
);

/** Shown when fewer than two simulations exist: explains the compare feature and points to a new simulation. */
const CompareNeedTwoNotice = ({ language, count, onNew }) => (
  <Card className="bg-lux-panel/90 border-lux-gold/20" data-testid="simulation-compare-need-two">
    <CardContent className="py-4 px-4 sm:px-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
      <div className="w-10 h-10 rounded-xl bg-lux-gold/10 flex items-center justify-center shrink-0">
        <GitCompareArrows className="w-5 h-5 text-[var(--lux-gold)]" />
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <p className="text-sm font-medium text-white">{t('compareNeedTwoTitle', language)}</p>
        <p className="text-xs text-white/50 leading-relaxed">{t('compareNeedTwoBody', language)}</p>
        {count === 1 && <p className="text-xs text-[var(--lux-amber)]" data-testid="simulation-compare-one-more">{t('compareNeedOneMore', language)}</p>}
      </div>
      <div className="shrink-0">
        <NewSimulationButton language={language} onNew={onNew} testId="simulation-compare-new-button" />
      </div>
    </CardContent>
  </Card>
);

const CompareToolbar = ({ language, active, selectedCount, canCompare, onToggle, onCompare }) => (
  <div className="flex flex-wrap items-center justify-end gap-2" data-testid="simulation-compare-toolbar">
    {active && (
      <span className="text-xs text-white/50" data-testid="simulation-compare-count">{selectedCount}/2 {t('compareSelected', language)}</span>
    )}
    {active && (
      <Button size="sm" onClick={onCompare} disabled={!canCompare} className="bg-[var(--lux-gold)] text-[var(--lux-ink)] hover:bg-[var(--lux-gold-2)] h-8" data-testid="simulation-compare-go">
        <GitCompareArrows className="w-4 h-4 mr-1" /> {t('compare', language)}
      </Button>
    )}
    <Button
      size="sm"
      variant="outline"
      onClick={onToggle}
      className={`h-8 border-white/10 text-white hover:bg-[var(--lux-panel-2)] ${active ? 'bg-lux-gold/10 border-lux-gold/40 text-[var(--lux-amber)]' : 'bg-[var(--lux-panel)]'}`}
      data-testid="simulation-compare-toggle"
    >
      {active ? <X className="w-4 h-4 mr-1" /> : <GitCompareArrows className="w-4 h-4 mr-1" />}
      {active ? t('consentCancel', language) : t('compareMode', language)}
    </Button>
  </div>
);

export const SimulationGallery = ({ simulations, loading, language, onOpen, onDelete, onCompare, onNew, retentionDays = 90 }) => {
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[0, 1, 2].map(i => <Skeleton key={i} className="h-56 w-full bg-white/5 rounded-xl" />)}
      </div>
    );
  }
  if (!simulations.length) {
    return (
      <Card className="bg-lux-panel/90 border-white/[0.08]" data-testid="simulations-empty">
        <CardContent className="py-14">
          <div className="text-center space-y-4 max-w-md mx-auto">
            <ScanFace className="w-8 h-8 text-white/10 mx-auto" />
            <p className="text-white/30 text-sm">{t('noSimulations', language)}</p>
            <p className="text-white/50 text-xs leading-relaxed">{t('compareNeedTwoBody', language)}</p>
            <NewSimulationButton language={language} onNew={onNew} testId="simulations-empty-new-button" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const canUseCompare = simulations.length >= 2;

  const toggleMode = () => {
    if (!compareMode && !canUseCompare) return;
    setCompareMode(!compareMode);
    setSelected([]);
  };

  const toggleSelect = (sessionId) => {
    setSelected(prev => {
      if (prev.includes(sessionId)) return prev.filter(id => id !== sessionId);
      if (prev.length >= 2) return [prev[1], sessionId];
      return [...prev, sessionId];
    });
  };

  const handleCardClick = (sessionId) => (compareMode ? toggleSelect(sessionId) : onOpen(sessionId));

  const labels = language === 'fr' ? ['AVANT', 'APR\u00c8S'] : ['BEFORE', 'AFTER'];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-white/40" data-testid="simulation-retention-note">{tf('retentionNote', language, { days: retentionDays })}</p>
        {canUseCompare && (
          <CompareToolbar
            language={language}
            active={compareMode}
            selectedCount={selected.length}
            canCompare={selected.length === 2}
            onToggle={toggleMode}
            onCompare={() => onCompare(selected[0], selected[1])}
          />
        )}
      </div>
      {compareMode && <p className="text-xs text-[var(--lux-amber)]" data-testid="simulation-compare-hint">{t('compareHint', language)}</p>}
      {!canUseCompare && <CompareNeedTwoNotice language={language} count={simulations.length} onNew={onNew} />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="simulation-gallery">
        {simulations.map(sim => {
          const isSelected = selected.includes(sim.session_id);
          return (
            <Card
              key={sim.session_id}
              className={`bg-lux-panel/90 transition-colors duration-200 overflow-hidden fade-in ${isSelected ? 'border-[var(--lux-gold)] ring-1 ring-lux-gold/60' : 'border-white/[0.08] hover:border-lux-gold/40'}`}
              data-testid={`simulation-card-${sim.session_id}`}
              data-selected={isSelected ? 'true' : 'false'}
            >
              <button onClick={() => handleCardClick(sim.session_id)} className="relative w-full flex gap-0.5 text-left" aria-label={compareMode ? t('compare', language) : t('openSimulation', language)} data-testid="simulation-thumb-button">
                <Thumb b64={sim.thumb_before} label={labels[0]} variant="before" />
                <Thumb b64={sim.thumb_after} label={labels[1]} variant="after" />
                {compareMode && (
                  <span className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center border ${isSelected ? 'bg-[var(--lux-gold)] border-[var(--lux-gold)] text-[var(--lux-ink)]' : 'bg-black/60 border-white/40 text-transparent'}`}>
                    <Check className="w-4 h-4" />
                  </span>
                )}
              </button>
              <CardContent className="py-3 flex items-center gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{t('savedSimulation', language)}</p>
                  <p className="text-xs text-white/30 flex items-center gap-1"><Clock className="w-3 h-3" /> {formatDate(sim.created_at, language)}</p>
                </div>
                <Button data-testid="simulation-gallery-open-button" variant="ghost" size="sm" onClick={() => onOpen(sim.session_id)} className="text-white/40 hover:text-[var(--lux-gold)] hover:bg-white/5" aria-label={t('openSimulation', language)}>
                  <ExternalLink className="w-4 h-4" />
                </Button>
                <Button data-testid="simulation-gallery-delete-button" variant="ghost" size="sm" onClick={() => onDelete(sim.session_id)} className="text-white/40 hover:text-[var(--lux-danger)] hover:bg-white/5" aria-label={t('deleteSession', language)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
